import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { useStepContext } from "../stepper.provider";
import PrimaryButton from "../../../buttons/PrimaryButton";
import { useReclamationContext } from "../../../../providers/reclamation/reclamation.provider";
import { compressVideo } from "../../../../util/compressVideo";

type IFormInput = {
  device: string;
  plan: string;
  coverage: string;
};

interface ICustomStepProps {
  colorPrimary: string;
  dobleAuth?: boolean;
}

const MAX_DOC_SIZE = 4_000_000; // 4 MB para PDF/JPG
const MAX_VIDEO_SIZE = 20_000_000; // 20 MB para video
// video/* permite escoger de galería/archivos en móvil; se listan también las
// extensiones para desktop.
const DOCUMENT_TYPES = ".pdf,.jpg,.jpeg,image/jpeg,image/jpg";
const VIDEO_TYPES = ".mp4,.mov,video/mp4,video/quicktime";

const DocumentStepComponent = ({
  colorPrimary,
  dobleAuth,
}: ICustomStepProps) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<any>();
  const { setCurrentStep } = useStepContext();
  const { documents, setGlobalForm } = useReclamationContext();

  // Estado de compresión por documento (id -> { processing, progress }).
  const [videoState, setVideoState] = useState<
    Record<string, { processing: boolean; progress: number }>
  >({});
  // Videos ya comprimidos, listos para reemplazar el original en el submit.
  const compressedRef = useRef<Record<string, File>>({});

  const isProcessing = Object.values(videoState).some((v) => v?.processing);

  const validateSize = (
    id: string,
    value: React.ChangeEvent<HTMLInputElement>,
    isVideo: boolean = false,
  ) => {
    const files = value.target.files;
    if (!files || files.length === 0) {
      clearErrors(id as any);
      return;
    }
    const file = files[0];
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_DOC_SIZE;
    const sizeMessage = isVideo ? "20MB" : "4MB";
    if (file.size > maxSize) {
      setError(id as any, {
        message: `Excediste el tamaño permitido de ${sizeMessage}`,
      });
    } else {
      clearErrors(id as any);
    }
  };

  const handleFileChange = async (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>,
    isVideo: boolean = false,
  ) => {
    const file = event.target.files?.[0];
    delete compressedRef.current[id];

    if (!file) {
      clearErrors(id as any);
      return;
    }

    // Documentos (PDF/JPG): validación de tamaño directa.
    if (!isVideo || !file.type.startsWith("video/")) {
      validateSize(id, event, isVideo);
      return;
    }

    // Video: si ya es menor a 20MB, aceptar sin comprimir.
    clearErrors(id as any);
    const originalSizeMB = (file.size / 1_000_000).toFixed(2);
    console.log(`[Video] Archivo seleccionado: "${file.name}"`);
    console.log(`[Video] Tamaño: ${originalSizeMB} MB`);

    if (file.size <= MAX_VIDEO_SIZE) {
      console.log(
        `[Video] ✓ El video ya es menor a 20MB, no requiere compresión`,
      );
      compressedRef.current[id] = file;
      clearErrors(id as any);
      return;
    }

    // Video supera 20MB: comprimir en el cliente antes de enviar.
    console.log(`[Video] ⚠ Video supera 20MB, iniciando compresión...`);
    const startTime = performance.now();

    setVideoState((state) => ({
      ...state,
      [id]: { processing: true, progress: 0 },
    }));
    try {
      const { file: output, compressed } = await compressVideo(file, {
        onProgress: (ratio) =>
          setVideoState((state) => ({
            ...state,
            [id]: { processing: true, progress: Math.round(ratio * 100) },
          })),
      });

      const endTime = performance.now();
      const durationSec = ((endTime - startTime) / 1000).toFixed(2);
      const compressedSizeMB = (output.size / 1_000_000).toFixed(2);
      const reductionPercent = ((1 - output.size / file.size) * 100).toFixed(1);

      console.log(`[Compresión] Compresión completada en ${durationSec}s`);
      console.log(`[Compresión] Tamaño comprimido: ${compressedSizeMB} MB`);
      console.log(`[Compresión] Reducción: ${reductionPercent}%`);
      console.log(
        `[Compresión] ¿Se comprimió?: ${compressed ? "Sí" : "No (archivo original)"}`,
      );

      compressedRef.current[id] = output;
      if (output.size > MAX_VIDEO_SIZE) {
        setError(id as any, {
          message:
            "El video supera 20MB incluso comprimido. Recórtalo e inténtalo de nuevo.",
        });
      } else {
        clearErrors(id as any);
      }
    } catch {
      setError(id as any, {
        message: "No se pudo procesar el video. Intenta con otro archivo.",
      });
    } finally {
      setVideoState((state) => ({
        ...state,
        [id]: { processing: false, progress: 100 },
      }));
    }
  };

  const onSubmit = (values: any) => {
    if (isProcessing) return;

    // Reemplazar cada video por su versión comprimida (formato [File] para
    // mantener la forma que espera send.step: documents[id][0]).
    const documentsValues = { ...values };
    let oversizeVideo = false;
    Object.entries(compressedRef.current).forEach(([id, compressed]) => {
      documentsValues[id] = [compressed];
      if (compressed.size > MAX_VIDEO_SIZE) {
        oversizeVideo = true;
        setError(id as any, {
          message:
            "El video supera 20MB incluso comprimido. Recórtalo e inténtalo de nuevo.",
        });
      }
    });
    if (oversizeVideo) return;

    setGlobalForm((prevState: any) => ({
      ...prevState,
      documents: documentsValues,
    }));
    setCurrentStep((prev) => prev + 1);
  };
  const handlePreviusStep = () => {
    setCurrentStep((prev) => prev - 1);
    compressedRef.current = {};
    setGlobalForm((prevState: any) => ({ ...prevState, documents: {} }));
  };

  return (
    <section>
      <h2 className="mb-5 text-center text-3xl font-bold">
        3. <span style={{ color: colorPrimary }}>Sube</span> tus documentos
      </h2>
      <div className="mb-10 w-full"></div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {documents
          ? documents.map((doc) =>
              dobleAuth ? (
                doc.nombre_documento_save !== "Formato de reclamación." && (
                  <>
                    <div className="mb-2 ml-1">
                      <p className="text-lg font-normal">
                        {doc.nombre_documento_save}
                        {doc.required && (
                          <>
                            <span className="text-3xl text-critical"> * </span>
                            {(errors as any)[doc.id] && (
                              <span className="text-critical">
                                {String((errors as any)[doc.id]?.message)}
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="relative inline-block w-full">
                      <label
                        className="left-1 z-10 inline-block h-10 w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-md bg-gray px-3 py-2 text-textBlack after:absolute after:right-0 after:top-0 after:block after:h-10 after:rounded-r-md after:bg-buttonBlack after:px-2 after:pt-2 after:text-center after:align-middle after:text-white after:content-['Seleccionar']"
                        htmlFor={`${doc.id}`}
                      >
                        {watch(`${doc.id}`)
                          ? watch(`${doc.id}`).length > 0 &&
                            watch(`${doc.id}`)[0].name
                          : null}
                      </label>

                      <input
                        {...register(`${doc.id}`, {
                          required: {
                            value: doc.required,
                            message: "Requerido",
                          },
                          onChange: (event) =>
                            handleFileChange(
                              `${doc.id}`,
                              event,
                              doc.type === "video",
                            ),
                        })}
                        id={`${doc.id}`}
                        type="file"
                        accept={
                          doc.type === "video" ? VIDEO_TYPES : DOCUMENT_TYPES
                        }
                        className="absolute left-0 top-0 -z-10 w-full opacity-0"
                      />
                      <div>
                        <small className="mt-1 text-sm text-textBlack">
                          {doc.type === "video"
                            ? "Video (MP4/MOV) máx. 20MB."
                            : "PDF o JPG máx. 4MB."}
                        </small>
                        {videoState[`${doc.id}`]?.processing && (
                          <small className="mt-1 block text-sm text-textBlack">
                            Comprimiendo video…{" "}
                            {videoState[`${doc.id}`]?.progress ?? 0}%
                          </small>
                        )}
                      </div>
                    </div>
                  </>
                )
              ) : (
                <>
                  <div className="mb-2 ml-1">
                    <p className="text-lg font-normal">
                      {doc.nombre_documento_save}
                      {doc.required && (
                        <>
                          <span className="text-critical"> * </span>
                          {(errors as any)[doc.id] && (
                            <span className="text-critical">
                              {String((errors as any)[doc.id]?.message)}
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="relative inline-block w-full">
                    <label
                      className="left-1 z-10 inline-block h-10 w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-md bg-gray px-3 py-2 text-textBlack after:absolute after:right-0 after:top-0 after:block after:h-10 after:rounded-r-md after:bg-buttonBlack after:px-2 after:pt-2 after:text-center after:align-middle after:text-white after:content-['Seleccionar']"
                      htmlFor={`${doc.id}`}
                    >
                      {watch(`${doc.id}`)
                        ? watch(`${doc.id}`).length > 0 &&
                          watch(`${doc.id}`)[0].name
                        : null}
                    </label>

                    <input
                      {...register(`${doc.id}`, {
                        required: {
                          value: doc.required,
                          message: "Requerido",
                        },
                        onChange: (event) =>
                          handleFileChange(
                            `${doc.id}`,
                            event,
                            doc.type === "video",
                          ),
                      })}
                      id={`${doc.id}`}
                      type="file"
                      accept={
                        doc.type === "video" ? VIDEO_TYPES : DOCUMENT_TYPES
                      }
                      className="absolute left-0 top-0 -z-10 w-full opacity-0"
                    />
                    <div>
                      <small className="mt-1 text-sm text-textBlack">
                        {doc.type === "video"
                          ? "Video (MP4/MOV) máx. 20MB."
                          : "PDF o JPG máx. 4MB."}
                      </small>
                      {videoState[`${doc.id}`]?.processing && (
                        <small className="mt-1 block text-sm text-textBlack">
                          Comprimiendo video…{" "}
                          {videoState[`${doc.id}`]?.progress ?? 0}%
                        </small>
                      )}
                    </div>
                  </div>
                </>
              ),
            )
          : null}
        <div className="mt-4 flex justify-between">
          <PrimaryButton
            title="Atras"
            className="rounded-2xl border px-5 text-textBlack"
            onClick={() => handlePreviusStep()}
          />
          <PrimaryButton
            type="submit"
            title="Siguiente"
            className="rounded-2xl bg-buttonBlack px-5 text-white"
            disabled={isProcessing}
            loading={isProcessing}
          />
        </div>
      </form>
    </section>
  );
};

export default DocumentStepComponent;
